// useEffectivePhase — wraps useElectionPhase() with a client-side URL
// override so the admin team (or anyone with the URL) can browse the
// homepage as readers WILL see it under a given phase WITHOUT
// flipping the production phase in the DB.
//
// Trigger model — mirrors the existing `?preview=1` feature-flag
// bypass pattern in src/components/FeatureSection.tsx:
//
//     /?phase=results_pending_rogue    → stick the override
//     /?phase=normal                   → switch to any other valid phase
//     /?phase=clear                    → drop the override, return to
//                                        the DB-driven phase
//
// The override is stored in localStorage under PHASE_OVERRIDE_KEY so
// it survives navigation within the SPA. ONLY a valid ElectionPhase
// value is accepted — typos fall through to the DB phase. The
// override never crosses the browser boundary; it can't change what
// other readers see.
//
// SAFETY: this only affects rendering. It does NOT call PUT on
// /api/admin/election-phase. The DB phase is untouched. So Vercel
// preview deploys, dev environments, and individual operator
// browsers can demo phases without affecting anyone else.

import { useEffect, useState } from 'react';
import { useElectionPhase, type UseElectionPhaseResult } from './useElectionPhase';
import {
  isElectionPhase,
  type ElectionPhase,
} from '../data/electionPhase-types';

export const PHASE_OVERRIDE_KEY = 'lm.election-phase-override';

/** Clear the phase override programmatically — used by the
 *  PreviewBanner's "Clear" affordance. Returns true if an override
 *  was set and is now cleared. */
export function clearPhaseOverride(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const had = window.localStorage.getItem(PHASE_OVERRIDE_KEY) !== null;
    window.localStorage.removeItem(PHASE_OVERRIDE_KEY);
    return had;
  } catch {
    return false;
  }
}

/** Resolved client-side phase override (or null = no override). Reads
 *  the URL once on mount, stickies in localStorage. Re-evaluated on
 *  popstate so SPA navigation refreshes the value. */
function useOverridePhase(): ElectionPhase | null {
  const [override, setOverride] = useState<ElectionPhase | null>(() => {
    if (typeof window === 'undefined') return null;
    return readOverride();
  });
  useEffect(() => {
    const onPop = () => setOverride(readOverride());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  return override;
}

function readOverride(): ElectionPhase | null {
  if (typeof window === 'undefined') return null;
  try {
    const sp = new URL(window.location.href).searchParams.get('phase');
    if (sp) {
      // 'clear' wipes the override.
      if (sp === 'clear') {
        try { window.localStorage.removeItem(PHASE_OVERRIDE_KEY); } catch { /* ignore */ }
        return null;
      }
      if (isElectionPhase(sp)) {
        try { window.localStorage.setItem(PHASE_OVERRIDE_KEY, sp); } catch { /* ignore */ }
        return sp;
      }
      // Invalid phase string — leave any sticky override in place
      // rather than silently flipping behaviour.
    }
    const stored = window.localStorage.getItem(PHASE_OVERRIDE_KEY);
    return isElectionPhase(stored) ? stored : null;
  } catch {
    return null;
  }
}

export interface UseEffectivePhaseResult extends UseElectionPhaseResult {
  /** The phase the UI should render. = overridePhase ?? dbPhase. */
  phase: ElectionPhase;
  /** True iff a client-side override is currently winning over the
   *  DB-resolved phase. Surfaces in the admin preview surface so the
   *  operator can see they're in preview mode. */
  isOverride: boolean;
  /** The DB-resolved phase (unfiltered). Lets the admin compare. */
  dbPhase: ElectionPhase;
}

export function useEffectivePhase(): UseEffectivePhaseResult {
  const db = useElectionPhase();
  const override = useOverridePhase();
  return {
    ...db,
    phase: override ?? db.phase,
    isOverride: override !== null,
    dbPhase: db.phase,
  };
}
